import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { getFormEntriesByFormName } from "../services/formServices";
import toast from "react-hot-toast";
import { FORMNAMES } from "../utilities/codes";
import { schoolContext } from "../contexts/SchoolContextProvider";
import { mediaSource, useBackgroundImage } from "../utilities/mediautilities";
import { APIENDPOINT } from "../services/apiHelper";
import { useTranslation } from "react-i18next";
import { I18N_KEYS } from "../languages/PlaceHolder.js";

const Dignitary = () => {
    const [selectedCard, setSelectedCard] = useState(null);
    const { schoolDetails } = useContext(schoolContext);
    const [dignitories, setDignitories] = useState([])
    const { t } = useTranslation()
    const bgUrl = useBackgroundImage(schoolDetails?.dignataries_back_image?.fileUrl);

    useEffect(() => {
        getDignatories();
    }, [])

    const getDignatories = async () => {
        try {
            const response = await getFormEntriesByFormName(FORMNAMES.SCHOOLDIGNATORIES);
            setDignitories(response);
        } catch (error) {
            toast.error(error.message);
        }
    }

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.15 }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeOut" }
        }
    };

    return (
        <>
            {dignitories?.length > 0 &&
                <>
                    <section
                        id="dignitaries"
                        className="py-20 px-6 relative bg-fixed bg-center bg-cover"
                        style={{ backgroundImage: `url(${bgUrl})` }}
                    >
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-black/70"></div>

                        <div className="relative z-10 max-w-7xl mx-auto">
                            {/* Heading */}
                            <motion.h2
                                initial={{ opacity: 0, y: -30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                                className="text-4xl md:text-4xl font-bold text-center text-accent"
                            >
                                {t(I18N_KEYS.DIGNATARIESTITLE)}
                            </motion.h2>

                            <motion.p
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.3 }}
                                className="text-accent mt-3 mb-14 text-center text-lg"
                            >
                                {schoolDetails?.dignitaries_subtitle && schoolDetails?.dignitaries_subtitle}
                            </motion.p>

                            {/* Cards */}
                            <motion.div
                                variants={containerVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                className="common-container   grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10"
                            >
                                {dignitories.map((dig, index) => (
                                    <motion.div
                                        key={index}
                                        variants={cardVariants}
                                        whileHover={{ scale: 1.05 }}
                                        className="bg-accent backdrop-blur-lg shadow-xl rounded-2xl text-center p-8 transition "
                                    >
                                        {/* Image */}
                                        <div className="h-40 w-40 mx-auto rounded-full overflow-hidden mb-5 border-4 border-primary">
                                            <motion.img
                                                src={mediaSource(dig?.payload?.profile_photo?.fileUrl)}
                                                alt={dig?.payload?.role}
                                                className="h-full w-full object-cover"
                                                whileHover={{ scale: 1.15 }}
                                                transition={{ duration: 0.4 }}
                                                onClick={() => setSelectedCard(dig)}
                                                onError={(e) => mediaSource(e)}
                                            />
                                        </div>

                                        {/* Content */}
                                        {dig?.payload?.role &&
                                            <h3 className="text-xl font-semibold mb-3 text-gray-800">
                                                {dig?.payload?.role}
                                            </h3>
                                        }
                                        {dig?.payload?.about &&
                                            <p className="text-gray-600 text-sm leading-relaxed"
                                                dangerouslySetInnerHTML={{
                                                    __html: dig?.payload?.about
                                                }}
                                            >
                                            </p>
                                        }
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </section>
                    <AnimatePresence>
                        {selectedCard && (
                            <motion.div
                                className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setSelectedCard(null)}
                            >
                                <button
                                    onClick={() => setSelectedCard(null)}
                                    className="absolute top-4 right-4 text-accent text-3xl font-bold hover:text-primary"
                                >
                                    &times;
                                </button>
                                <motion.div
                                    className="bg-accent rounded-2xl overflow-hidden shadow-2xl max-w-3xl w-full"
                                    initial={{ scale: 0.8, y: 40 }}
                                    animate={{ scale: 1, y: 0 }}
                                    exit={{ scale: 0.8, y: 40 }}
                                    transition={{ duration: 0.3 }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <img
                                        src={mediaSource(selectedCard?.payload?.profile_photo?.fileUrl)}
                                        alt={selectedCard?.payload?.role}
                                        className="w-full h-full object-cover"
                                        onError={(e) => mediaSource(e)}
                                    />

                                    {(selectedCard?.payload?.role || selectedCard?.payload?.about) &&
                                        <div className="p-6">
                                            {selectedCard?.payload?.role &&
                                                <h3 className="text-2xl font-bold mb-2">
                                                    {selectedCard?.payload?.role}
                                                </h3>
                                            }
                                            {selectedCard?.payload?.about &&
                                                <p className="text-gray-600"
                                                    dangerouslySetInnerHTML={{
                                                        __html: selectedCard?.payload?.about
                                                    }}
                                                >
                                                </p>
                                            }
                                        </div>
                                    }

                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </>
            }
        </>

    );
}

export default Dignitary;
